import { Phone, MapPin, CheckCircle, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const IslingtonPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Car Recovery Islington | Vehicle Transport N1 | 24/7 Breakdown Service Islington"
        description="Fast car recovery in Islington N1. 30-minute response time. 24/7 breakdown service, vehicle transport, Angel, Highbury. Call 07492 639223 for Islington car recovery."
        keywords="car recovery islington, breakdown service islington, car recovery n1, islington vehicle transport, angel car recovery, highbury breakdown service"
      />
      <Header />

      <section className="bg-gradient-to-br from-green-900 via-emerald-800 to-teal-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              📍 Local Islington Service
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Car Recovery & Vehicle Transport in Islington
            </h1>
            <p className="text-xl text-emerald-100 mb-8">
              Fast 30-minute car recovery across Islington N1. Available 24/7 for breakdowns, vehicle transport, and emergency recovery in Angel, Highbury, Barnsbury, and all Islington areas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-emerald-900 hover:bg-slate-100 text-lg px-8 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-emerald-900 text-lg px-8 py-6">
                  Get Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-600">30 Min</p>
              <p className="text-sm text-slate-600">Avg Response</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-600">24/7</p>
              <p className="text-sm text-slate-600">Always Available</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-600">N1</p>
              <p className="text-sm text-slate-600">Full Coverage</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-600">Local</p>
              <p className="text-sm text-slate-600">Islington Based</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Professional Car Recovery Services in Islington
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Ovawell Autos provides fast, professional car recovery and vehicle transport services across all Islington areas. From Angel to Highbury, Barnsbury to Canonbury, our local team knows every street in N1 and delivers an average 30-minute response time for all breakdown and recovery situations.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Islington Areas We Cover
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {[
                "Angel N1",
                "Highbury N5",
                "Barnsbury N1",
                "Canonbury N1",
                "Kings Cross N1",
                "Pentonville N1",
                "Clerkenwell EC1",
                "Finsbury N1",
                "Caledonian Road",
                "Upper Street",
                "Essex Road",
                "Holloway Road"
              ].map((area, index) => (
                <div key={index} className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-slate-700">{area}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Our Islington Recovery Services
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "24/7 breakdown recovery",
                "Vehicle transportation",
                "Emergency roadside assistance",
                "Jump start services",
                "Accident recovery",
                "Battery boost service",
                "Flat tyre recovery",
                "Fuel delivery",
                "Non-running vehicle transport",
                "Keys locked in car",
                "Car auction transport",
                "Urgent towing service"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Why Choose Ovawell Autos in Islington?
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Clock className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Fast Local Response</h4>
                      <p className="text-slate-600">
                        Based in East London with excellent access to Islington. We reach Angel in 20 minutes, Highbury in 25, anywhere in N1 within 30 minutes on average.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">We Know Islington Streets</h4>
                      <p className="text-slate-600">
                        From busy Upper Street to quiet residential roads in Barnsbury, we navigate all areas. We understand parking restrictions, one-way systems, and best recovery routes.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">24/7 Service - No Hidden Fees</h4>
                      <p className="text-slate-600">
                        Available every day of the year. Transparent pricing with no night surcharges or hidden fees. Get a quote before we dispatch.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-emerald-900 to-green-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Car Recovery in Islington?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            24/7 car recovery and vehicle transport service across Islington N1 and surrounding areas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="bg-white text-emerald-900 hover:bg-slate-100 text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call {companyInfo.phone}
              </Button>
            </a>
            <Link to="/#quote">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-emerald-900 text-lg px-8 py-6">
                Get Quote Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
